import { z } from 'zod';
import { CONTEST_RULES, EDITORIAL_VISIBILITIES, JUDGE_MODES, PROBLEM_DIFFICULTIES, PROBLEM_STATUSES, } from './enums.js';
import { PAGINATION, SYSTEM_LIMITS } from './limits.js';
/**
 * すべての外部入力はここのスキーマを通す (SPEC 61)。
 * API 層でも judge 層でも同じ定義を使い、片方だけ緩いという状態を作らない。
 */
/** UTF-8 バイト長で測る。`length` は surrogate pair で実態とずれる。 */
const byteLength = (value) => new TextEncoder().encode(value).length;
export const uuidSchema = z.string().uuid();
export const problemSlugSchema = z
    .string()
    .regex(/^[A-Z0-9][A-Z0-9_-]{0,31}$/, 'problem slug は大文字英数字・ハイフン・アンダースコアのみ');
/**
 * 問題 ID からコンテストとラベルを読み取る (SPEC 34)。
 *
 * `CR001_A` は「コンテスト cr001 の A 問題」を表す、という運用上の決まりを
 * ここに 1 か所だけ書く。問題を作るたびに手で割り当て直すのは間違いのもとで、
 * 実際に割り当て忘れたまま公開された。
 *
 * 対応するコンテストが無ければ null ではなく「見つからない」として扱い、
 * 問題の作成自体は成功させる。コンテストに属さない常設問題もあるため。
 */
export function parseProblemSlug(slug) {
    // 末尾の _<ラベル> だけを見る。前半にアンダースコアがあっても構わない。
    const match = /^(.+)_([A-Z][A-Z0-9]{0,3})$/.exec(slug);
    if (!match?.[1] || !match[2]) {
        return null;
    }
    const contestSlug = match[1].toLowerCase();
    // コンテスト ID として成立しない形なら諦める。
    if (!/^[a-z0-9][a-z0-9_-]{0,47}$/.test(contestSlug)) {
        return null;
    }
    return { contestSlug, label: match[2] };
}
/** ラベルから並び順を決める。A=1, B=2 … 4 文字までの英数字に耐える。 */
export function labelToOrder(label) {
    let order = 0;
    for (const ch of label) {
        order = order * 36 + parseInt(ch, 36);
    }
    return order;
}
export const contestSlugSchema = z
    .string()
    .regex(/^[a-z0-9][a-z0-9_-]{0,47}$/, 'contest slug は小文字英数字・ハイフン・アンダースコアのみ');
export const usernameSchema = z
    .string()
    .regex(/^[A-Za-z0-9_]{3,24}$/, 'username は英数字とアンダースコア 3〜24 文字');
export const languageSlugSchema = z.string().regex(/^[a-z0-9][a-z0-9_.-]{0,31}$/);
/**
 * ソースコード。
 * - サイズはバイト数で制限する（DB の CHECK 制約と同値）
 * - NUL バイトを拒否する。Postgres の text 型が保存できず、
 *   さらにファイル書き出し時に予期しない切り詰めを起こすため。
 */
export const sourceCodeSchema = z
    .string()
    .min(1, 'ソースコードが空です')
    .refine((v) => byteLength(v) <= SYSTEM_LIMITS.MAX_SOURCE_CODE_BYTES, {
    message: `ソースコードは ${SYSTEM_LIMITS.MAX_SOURCE_CODE_BYTES} バイト以内にしてください`,
})
    .refine((v) => !v.includes('\u0000'), { message: 'NUL 文字は使用できません' });
export const customTestInputSchema = z
    .string()
    .refine((v) => byteLength(v) <= SYSTEM_LIMITS.MAX_CUSTOM_TEST_INPUT_BYTES, {
    message: `入力は ${SYSTEM_LIMITS.MAX_CUSTOM_TEST_INPUT_BYTES} バイト以内にしてください`,
})
    .refine((v) => !v.includes('\u0000'), { message: 'NUL 文字は使用できません' });
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce
        .number()
        .int()
        .min(1)
        .max(PAGINATION.MAX_PER_PAGE)
        .default(PAGINATION.DEFAULT_PER_PAGE),
});
// -----------------------------------------------------------------------------
// Submissions
// -----------------------------------------------------------------------------
export const createSubmissionSchema = z.object({
    problemSlug: problemSlugSchema,
    languageSlug: languageSlugSchema,
    sourceCode: sourceCodeSchema,
    /** contest 中の提出。省略時は practice 扱い。 */
    contestSlug: contestSlugSchema.optional(),
});
export const createCustomTestSchema = z.object({
    problemSlug: problemSlugSchema.optional(),
    languageSlug: languageSlugSchema,
    sourceCode: sourceCodeSchema,
    input: customTestInputSchema,
});
export const submissionListQuerySchema = paginationSchema.extend({
    problemSlug: problemSlugSchema.optional(),
    contestSlug: contestSlugSchema.optional(),
    username: usernameSchema.optional(),
    status: z.string().optional(),
    languageSlug: languageSlugSchema.optional(),
});
// -----------------------------------------------------------------------------
// Problems
// -----------------------------------------------------------------------------
export const problemListQuerySchema = paginationSchema.extend({
    q: z.string().max(100).optional(),
    difficulty: z.enum(PROBLEM_DIFFICULTIES).optional(),
    tag: z.string().max(32).optional(),
    /** 認証済みのときのみ有効。 */
    solved: z.enum(['solved', 'unsolved', 'attempted']).optional(),
    sort: z
        .enum(['newest', 'oldest', 'difficulty', 'acceptance', 'solved'])
        .default('newest'),
});
export const problemWriteSchema = z.object({
    slug: problemSlugSchema,
    title: z.string().min(1).max(200),
    statement: z.string().max(200_000).default(''),
    inputFormat: z.string().max(50_000).default(''),
    outputFormat: z.string().max(50_000).default(''),
    constraints: z.string().max(50_000).default(''),
    timeLimitMs: z.number().int().min(100).max(SYSTEM_LIMITS.MAX_TIME_LIMIT_MS),
    memoryLimitMb: z.number().int().min(16).max(SYSTEM_LIMITS.MAX_MEMORY_LIMIT_MB),
    difficulty: z.enum(PROBLEM_DIFFICULTIES),
    difficultyScore: z.number().int().min(0).max(4000).nullable().default(null),
    judgeMode: z.enum(JUDGE_MODES).default('IGNORE_TRAILING_WHITESPACE'),
    floatTolerance: z.number().positive().nullable().default(null),
    status: z.enum(PROBLEM_STATUSES).default('DRAFT'),
    tagSlugs: z.array(z.string().max(32)).max(20).default([]),
});
export const problemUpdateSchema = problemWriteSchema.partial().omit({ slug: true });
// -----------------------------------------------------------------------------
// Testcases (Admin)
// -----------------------------------------------------------------------------
export const testcaseWriteSchema = z.object({
    name: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/),
    input: z.string(),
    expectedOutput: z.string(),
    isSample: z.boolean().default(false),
    groupName: z.string().max(64).nullable().default(null),
    displayOrder: z.number().int().min(0).default(0),
    score: z.number().int().min(0).default(0),
});
export const testcaseBulkSchema = z.object({
    testcases: z.array(testcaseWriteSchema).min(1).max(SYSTEM_LIMITS.MAX_TESTCASES_PER_PROBLEM),
    /** true なら既存の testcase をすべて置き換える。 */
    replace: z.boolean().default(false),
});
export const testcaseGroupWriteSchema = z.object({
    name: z.string().min(1).max(64),
    displayOrder: z.number().int().min(0).default(0),
    score: z.number().int().min(0).max(10_000).default(0),
});
// -----------------------------------------------------------------------------
// Contests
// -----------------------------------------------------------------------------
export const contestWriteSchema = z
    .object({
    slug: contestSlugSchema,
    title: z.string().min(1).max(200),
    description: z.string().max(200_000).default(''),
    startTime: z.string().datetime({ offset: true }),
    endTime: z.string().datetime({ offset: true }),
    registrationStart: z.string().datetime({ offset: true }).nullable().default(null),
    registrationEnd: z.string().datetime({ offset: true }).nullable().default(null),
    freezeTime: z.string().datetime({ offset: true }).nullable().default(null),
    rated: z.boolean().default(false),
    rule: z.enum(CONTEST_RULES).default('ICPC'),
    penaltyMinutes: z.number().int().min(0).max(1440).default(5),
})
    .refine((v) => new Date(v.endTime) > new Date(v.startTime), {
    message: 'endTime は startTime より後である必要があります',
    path: ['endTime'],
});
/**
 * コンテストの状態遷移 (SPEC 35, 87)。
 *
 * 状態を直接書き換えさせず、「何をしたいか」で受け取る。
 * 遷移の正当性はサーバー側で検証する（例: DRAFT からいきなり RUNNING にはできない）。
 */
export const CONTEST_TRANSITIONS = [
    'publish',
    'unpublish',
    'start',
    'end',
    'archive',
];
export const contestTransitionSchema = z.object({
    action: z.enum(CONTEST_TRANSITIONS),
});
export const contestUpdateSchema = z
    .object({
    title: z.string().min(1).max(200).optional(),
    description: z.string().max(200_000).optional(),
    startTime: z.string().datetime({ offset: true }).optional(),
    endTime: z.string().datetime({ offset: true }).optional(),
    registrationStart: z.string().datetime({ offset: true }).nullable().optional(),
    registrationEnd: z.string().datetime({ offset: true }).nullable().optional(),
    freezeTime: z.string().datetime({ offset: true }).nullable().optional(),
    rated: z.boolean().optional(),
    rule: z.enum(CONTEST_RULES).optional(),
    penaltyMinutes: z.number().int().min(0).max(1440).optional(),
})
    .refine((v) => v.startTime === undefined ||
    v.endTime === undefined ||
    new Date(v.endTime) > new Date(v.startTime), { message: 'endTime は startTime より後である必要があります', path: ['endTime'] });
/** コンテストへの問題割り当て (SPEC 36)。 */
export const contestProblemsSchema = z.object({
    problems: z
        .array(z.object({
        problemSlug: problemSlugSchema,
        label: z.string().regex(/^[A-Z][A-Z0-9]{0,3}$/),
        score: z.number().int().min(1).max(100_000),
    }))
        .max(26)
        .refine((list) => new Set(list.map((p) => p.label)).size === list.length, { message: 'ラベルが重複しています' })
        .refine((list) => new Set(list.map((p) => p.problemSlug)).size === list.length, { message: '同じ問題が複数回指定されています' }),
});
// -----------------------------------------------------------------------------
// Profile
// -----------------------------------------------------------------------------
export const profileUpdateSchema = z.object({
    username: usernameSchema.optional(),
    displayName: z.string().min(1).max(48).optional(),
    avatarUrl: z.string().url().max(500).nullable().optional(),
    bio: z.string().max(1000).optional(),
    shareSubmissions: z.boolean().optional(),
});
// -----------------------------------------------------------------------------
// Editorial / Clarification
// -----------------------------------------------------------------------------
export const editorialWriteSchema = z.object({
    content: z.string().max(200_000),
    visibility: z.enum(EDITORIAL_VISIBILITIES).default('AFTER_CONTEST'),
});
export const clarificationCreateSchema = z.object({
    problemSlug: problemSlugSchema.optional(),
    question: z.string().min(1).max(4000),
});
export const clarificationAnswerSchema = z.object({
    answer: z.string().min(1).max(4000),
    isPublic: z.boolean().default(false),
});
// -----------------------------------------------------------------------------
// Rejudge (Admin)
// -----------------------------------------------------------------------------
export const rejudgeSchema = z
    .object({
    submissionId: uuidSchema.optional(),
    problemSlug: problemSlugSchema.optional(),
    contestSlug: contestSlugSchema.optional(),
})
    .refine((v) => [v.submissionId, v.problemSlug, v.contestSlug].filter(Boolean).length === 1, { message: 'submissionId / problemSlug / contestSlug のいずれか 1 つを指定してください' });
/**
 * WJ のまま止まった提出の再投入 (SPEC 56, 66)。
 *
 * worker が落ちると、queue から取り出された直後の提出が JUDGING のまま残る。
 * job はもう queue に無いので、放っておいても誰も判定しない。
 *
 * 判定中のものまで巻き込まないよう、提出からの経過時間で絞る。
 */
export const requeuePendingSchema = z.object({
    /** 提出からこの分数より長く WJ のままのものだけを対象にする。 */
    olderThanMinutes: z.number().int().min(0).max(1440).default(5),
    /** 対象を 1 問に絞りたいとき。 */
    problemSlug: problemSlugSchema.optional(),
});
//# sourceMappingURL=schemas.js.map